import { Link } from "react-router-dom";
import { BookOpen, Gamepad2, Wind, ClipboardCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";

interface Segment {
  id: string;
  labelKey: "learn" | "trainBrain" | "feelBetter" | "checkIn";
  helperKey: "learnHelper" | "trainBrainHelper" | "feelBetterHelper" | "checkInHelper";
  route: string;
  icon: typeof BookOpen;
  bg: string;
  motion: string;
}

const segments: Segment[] = [
  { id: "learn", labelKey: "learn", helperKey: "learnHelper", route: "/articles", icon: BookOpen, bg: "tile-bg-teal", motion: "card-motion-page" },
  { id: "train", labelKey: "trainBrain", helperKey: "trainBrainHelper", route: "/games", icon: Gamepad2, bg: "tile-bg-orange", motion: "card-motion-play" },
  { id: "feel", labelKey: "feelBetter", helperKey: "feelBetterHelper", route: "/exercises", icon: Wind, bg: "tile-bg-pink", motion: "card-motion-breathe" },
  { id: "check", labelKey: "checkIn", helperKey: "checkInHelper", route: "/questionnaires", icon: ClipboardCheck, bg: "tile-bg-purple", motion: "card-motion-check" },
];

const HeroCircle = () => {
  const { t } = useTranslation();

  return (
    <div className="w-full">
      <p className="text-foreground mb-7 text-xs font-bold uppercase tracking-[0.2em] text-left">
        {t.home.heroCircle.centerText}
      </p>
      <div className="grid grid-cols-2 gap-3 sm:gap-5 pb-8">
        {segments.map((s) => {
          const Icon = s.icon;
          return (
            <Link
              key={s.id}
              to={s.route}
              aria-label={`${t.home.heroCircle[s.labelKey]} - ${t.home.heroCircle[s.helperKey]}`}
              className={cn(
                "group relative overflow-hidden rounded-2xl p-4 sm:p-7 md:p-8 min-h-[190px] sm:min-h-[230px] md:min-h-[270px]",
                "flex flex-col justify-between text-left transition-all duration-500",
                "shadow-[0_8px_20px_-8px_hsl(var(--tile-ink)/0.28),0_2px_6px_-2px_hsl(var(--tile-ink)/0.16)] hover:shadow-[0_14px_28px_-10px_hsl(var(--tile-ink)/0.36)] hover:-translate-y-1 active:translate-y-0",
                "outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-4",
                s.bg
              )}
            >
              <div className="flex justify-end items-start">
                <div className={cn("w-9 h-9 sm:w-11 sm:h-11 rounded-full border border-white/40 bg-white/20 flex items-center justify-center text-white transition-transform duration-500", s.motion)}>
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
              </div>
              <div>
                <span className="block text-xl sm:text-2xl md:text-4xl font-semibold leading-[1.1] tracking-tight text-white">
                  {t.home.heroCircle[s.labelKey]}
                </span>
                <span className="block mt-3 text-xs sm:text-sm md:text-base font-medium text-white/90 leading-relaxed max-w-md">
                  {t.home.heroCircle[s.helperKey]}
                </span>
                <span aria-hidden="true" className="block mt-5 w-10 border-t border-white/50 transition-[width] duration-500 group-hover:w-full" />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default HeroCircle;
